import { useExecuteCampaignAnalysisMutation } from '@/features/campaign/hooks/mutation';
import type { CreateCampaignAnalysisRequest } from '@/entities/campaign/generated/campaign-type';
import { CAMPAIGN_ANALYSIS_STATUS } from '@/entities/campaign/generated/campaign-constants';
import { useWorkspaceUsageQuery } from '@/entities/workspace/query';
import { Stage } from '@syncly/ui/stage';
import { useNavigate, useParams } from '@tanstack/react-router';
import { useState } from 'react';

import { useCampaignAnalysisHistory } from './hooks';
import { useCampaignAnalysisRun } from './hooks';
import {
  CAMPAIGN_ANALYSIS_REPORT_LIMIT_REACHED,
  FALLBACK_CAMPAIGN_ANALYSIS_REPORT_USAGE,
  isCampaignAnalysisReportUsageAtLimit,
} from './helpers/analysis-report-usage';
import { CampaignAnalysisContent } from './ui';
import { CampaignAnalysisCreateDialog } from './ui';
import { CampaignAnalysisEmptyState } from './ui';
import { CampaignAnalysisHistory } from './ui';
import { CampaignAnalysisRunPlaceholder } from './ui';
import { CampaignAnalysisStartButton } from './ui';
import { CampaignAnalysisErrorState, CampaignAnalysisLoadingState } from './ui';

type CampaignAnalysisSectionProps = {
  campaignId: string;
};

export function CampaignAnalysisSection({ campaignId }: CampaignAnalysisSectionProps) {
  const navigate = useNavigate();
  const { workspaceId } = useParams({
    from: '/workspace/$workspaceId/campaigns/$campaignId/',
  });
  const sectionId = `campaign-analysis-${campaignId}`;
  const history = useCampaignAnalysisHistory(campaignId);
  const selectedRunState = useCampaignAnalysisRun(campaignId, history.selectedRun);
  const executeAnalysis = useExecuteCampaignAnalysisMutation();
  const workspaceUsage = useWorkspaceUsageQuery();
  const reportUsage =
    workspaceUsage.data?.campaign_analysis ?? FALLBACK_CAMPAIGN_ANALYSIS_REPORT_USAGE;
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const hasHistoryData = history.data != null;
  const hasReachedReportLimit = isCampaignAnalysisReportUsageAtLimit(reportUsage);
  const activeRun = history.runs.find(
    (run) =>
      (run.run_id === history.selectedRun?.run_id ? selectedRunState.status : run.status) ===
        CAMPAIGN_ANALYSIS_STATUS.REQUESTING ||
      (run.run_id === history.selectedRun?.run_id ? selectedRunState.status : run.status) ===
        CAMPAIGN_ANALYSIS_STATUS.PROCESSING,
  );
  const startDisabledReason = activeRun
    ? 'An analysis is already in progress for this campaign.'
    : hasReachedReportLimit
      ? CAMPAIGN_ANALYSIS_REPORT_LIMIT_REACHED
      : undefined;

  const createAnalysis = async (request: CreateCampaignAnalysisRequest) => {
    const response = await executeAnalysis.mutateAsync({
      campaignId,
      request,
    });
    history.selectRun(response.run_id);
  };

  const openDiscovery = (prompt: string) => {
    navigate({
      to: '/workspace/$workspaceId/discovery/',
      params: { workspaceId },
      search: { prompt },
    });
  };

  return (
    <section aria-labelledby={`${sectionId}-title`} className="flex flex-col gap-4">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <h2 id={`${sectionId}-title`} className="text-foreground text-xl leading-6 font-semibold">
          Analysis
        </h2>
        {history.runs.length > 0 ? (
          <CampaignAnalysisStartButton
            disabledReason={startDisabledReason}
            onStartAnalysis={() => setIsCreateDialogOpen(true)}
            reportUsage={reportUsage}
          />
        ) : null}
      </div>

      <Stage>
        <Stage.Scene status={history.isLoading && !hasHistoryData}>
          <CampaignAnalysisLoadingState />
        </Stage.Scene>
        <Stage.Scene status={history.isError && !hasHistoryData}>
          <CampaignAnalysisErrorState onRetry={() => void history.refetch()} />
        </Stage.Scene>
        <Stage.Scene status={hasHistoryData && history.runs.length === 0}>
          <CampaignAnalysisEmptyState
            disabledReason={startDisabledReason}
            onStartAnalysis={() => setIsCreateDialogOpen(true)}
            reportUsage={reportUsage}
          />
        </Stage.Scene>
        <Stage.Scene data={history.runs.length > 0 ? history.selectedRun : null}>
          {(selectedRun) => (
            <div className="grid min-h-105 gap-6 lg:grid-cols-[220px_minmax(0,1fr)] lg:items-start">
              <CampaignAnalysisHistory
                hasNextPage={history.hasNextPage}
                isFetchingNextPage={history.isFetchingNextPage}
                isNextPageError={history.isFetchNextPageError}
                onLoadMore={() => void history.fetchNextPage()}
                onSelectRun={history.selectRun}
                runs={history.runs}
                selectedRunId={history.selectedRunId ?? selectedRun.run_id}
              />
              <Stage className="min-w-0">
                <Stage.Scene
                  data={
                    selectedRunState.status === CAMPAIGN_ANALYSIS_STATUS.COMPLETED
                      ? selectedRun
                      : null
                  }
                >
                  {(run) => (
                    <CampaignAnalysisContent
                      campaignId={campaignId}
                      onDeleted={history.clearSelection}
                      onOpenDiscovery={openDiscovery}
                      run={run}
                    />
                  )}
                </Stage.Scene>
                <Stage.Scene default>
                  <CampaignAnalysisRunPlaceholder
                    failureReason={selectedRunState.failureReason}
                    isStatusError={selectedRunState.isStatusError}
                    onRetryStatus={() => void selectedRunState.refetchStatus()}
                    run={selectedRun}
                    status={selectedRunState.status}
                  />
                </Stage.Scene>
              </Stage>
            </div>
          )}
        </Stage.Scene>
      </Stage>

      <CampaignAnalysisCreateDialog
        campaignId={campaignId}
        isOpen={isCreateDialogOpen}
        onClose={() => setIsCreateDialogOpen(false)}
        reportUsage={reportUsage}
        onSubmit={createAnalysis}
      />
    </section>
  );
}
